import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, ArrowLeft, BookOpen, Heart, Brain, HandHeart,
  Flame, Award, TrendingUp, Target, Sparkles, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const badgeIcons = {
  'book-open': BookOpen,
  'graduation-cap': Award,
  'users': Award,
  'flame': Flame,
  'heart': Heart,
  'hand-heart': HandHeart,
  'brain': Brain,
  'baby': Heart,
  'sparkles': Sparkles,
  'book-marked': BookOpen
};

export default function MindDashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) fetchDashboard();
    else setLoading(false);
  }, [user]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/foundation/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setDashboard(await res.json());
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-purple-50/30" data-testid="mind-dashboard">
        <div className="bg-gradient-to-br from-purple-900 to-indigo-900 text-white py-12">
          <div className="container mx-auto px-4">
            <Link to="/foundation" className="inline-flex items-center text-purple-200 hover:text-white mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Foundation
            </Link>
            <div className="flex items-center gap-3">
              <LayoutDashboard className="h-8 w-8" />
              <h1 className="text-3xl font-bold">Mind Dashboard</h1>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-12">
          <Card className="max-w-md mx-auto bg-purple-50 border-purple-200">
            <CardContent className="py-12 text-center">
              <LayoutDashboard className="h-12 w-12 text-purple-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-stone-800 mb-2">Track Your Growth</h2>
              <p className="text-stone-600 mb-6">Log in to see your learning progress, reflections, service hours, and badges.</p>
              <Link to="/login">
                <Button className="bg-purple-600 hover:bg-purple-700">Log In</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-purple-50/30" data-testid="mind-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-900 to-indigo-900 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/foundation" className="inline-flex items-center text-purple-200 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Foundation
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <LayoutDashboard className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Mind Dashboard</h1>
          </div>
          <p className="text-purple-100">Your unified growth tracker across all pillars.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Monthly Summary Stats */}
        <section>
          <h2 className="text-xl font-bold text-stone-800 mb-4">This Month</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-indigo-500 to-blue-600 text-white">
              <CardContent className="py-6">
                <BookOpen className="h-8 w-8 mb-2 opacity-80" />
                <div className="text-3xl font-bold">{dashboard?.monthly_summary?.courses_completed || 0}</div>
                <p className="text-indigo-100">Courses Completed</p>
                <p className="text-xs text-indigo-200 mt-1">
                  {dashboard?.monthly_summary?.courses_in_progress || 0} in progress
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-rose-500 to-pink-600 text-white">
              <CardContent className="py-6">
                <Flame className="h-8 w-8 mb-2 opacity-80" />
                <div className="text-3xl font-bold">{dashboard?.monthly_summary?.reflection_streak || 0}</div>
                <p className="text-rose-100">Day Reflection Streak</p>
                <p className="text-xs text-rose-200 mt-1">
                  {dashboard?.monthly_summary?.total_reflections || 0} total reflections
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
              <CardContent className="py-6">
                <HandHeart className="h-8 w-8 mb-2 opacity-80" />
                <div className="text-3xl font-bold">{dashboard?.monthly_summary?.service_hours || 0}</div>
                <p className="text-amber-100">Service Hours</p>
                {dashboard?.service_ledger?.next_benefit && (
                  <p className="text-xs text-amber-200 mt-1">
                    {dashboard.service_ledger.hours_to_next} to next benefit
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
              <CardContent className="py-6">
                <Brain className="h-8 w-8 mb-2 opacity-80" />
                <div className="text-3xl font-bold">{dashboard?.monthly_summary?.wellness_checkins || 0}</div>
                <p className="text-emerald-100">Wellness Check-ins</p>
                <p className="text-xs text-emerald-200 mt-1">This month</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Growth Nudges */}
        {dashboard?.growth_nudges?.length > 0 && (
          <section>
            <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-600" />
                  Recommended Next Steps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard.growth_nudges.map((nudge, idx) => (
                    <div key={`item-${idx}`} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <ChevronRight className="h-5 w-5 text-purple-500" />
                      <span className="text-stone-700">{nudge}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Badges */}
          <section>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-amber-500" />
                    Foundation Badges
                  </span>
                  <Badge variant="outline">
                    {dashboard?.badges?.earned || 0} / {dashboard?.badges?.total || 10}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-3">
                  {dashboard?.badges?.display?.map((badge) => {
                    const Icon = badgeIcons[badge.icon] || Award;
                    
                    return (
                      <motion.div
                        key={badge.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`p-3 rounded-xl text-center ${
                          badge.earned 
                            ? 'bg-amber-100 border-2 border-amber-300' 
                            : 'bg-stone-100 border-2 border-stone-200 opacity-50'
                        }`}
                        title={badge.name}
                      >
                        <Icon className={`h-6 w-6 mx-auto ${badge.earned ? 'text-amber-600' : 'text-stone-400'}`} />
                        <p className="text-xs mt-1 truncate font-medium">{badge.name}</p>
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Service Ledger Progress */}
          <section>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-amber-500" />
                  Service Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.service_ledger?.next_benefit ? (
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium text-stone-800">Next: {dashboard.service_ledger.next_benefit.benefit}</span>
                        <span className="text-stone-500">
                          {dashboard.service_ledger.total_hours} / {dashboard.service_ledger.next_benefit.threshold_hours} hrs
                        </span>
                      </div>
                      <Progress 
                        value={(dashboard.service_ledger.total_hours / dashboard.service_ledger.next_benefit.threshold_hours) * 100} 
                        className="h-3"
                      />
                    </div>
                    <Link to="/foundation/service">
                      <Button variant="outline" className="w-full">
                        <HandHeart className="h-4 w-4 mr-2" /> Log Service Hours
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Award className="h-10 w-10 text-amber-500 mx-auto mb-2" />
                    <p className="text-stone-600">All service benefits unlocked!</p>
                    <p className="text-2xl font-bold text-amber-600 mt-2">
                      {dashboard?.service_ledger?.total_hours || 0} hours
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        </div>

        {/* Connected Platforms */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-500" />
                Connected 7Fs Activity
              </CardTitle>
              <CardDescription>Your growth across the DowUrk ecosystem</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Heart className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">Faith Platform</p>
                      <p className="text-sm text-stone-500">
                        {dashboard?.connected_platforms?.faith?.practices_completed || 0} practices completed
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-rose-100 rounded-lg">
                      <Heart className="h-5 w-5 text-rose-600" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">Fitness Platform</p>
                      <p className="text-sm text-stone-500">
                        {dashboard?.connected_platforms?.fitness?.sessions_completed || 0} sessions completed
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Quick Actions */}
        <section>
          <div className="grid sm:grid-cols-4 gap-4">
            <Link to="/foundation/school">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardContent className="py-6 text-center">
                  <BookOpen className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                  <p className="font-medium text-stone-800">Continue Learning</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/foundation/character">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardContent className="py-6 text-center">
                  <Heart className="h-8 w-8 text-rose-600 mx-auto mb-2" />
                  <p className="font-medium text-stone-800">Daily Reflection</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/foundation/service">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardContent className="py-6 text-center">
                  <HandHeart className="h-8 w-8 text-amber-600 mx-auto mb-2" />
                  <p className="font-medium text-stone-800">Log Service</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/foundation/wellness">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardContent className="py-6 text-center">
                  <Brain className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                  <p className="font-medium text-stone-800">Wellness Check-in</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
